<template>
  <section class="w-full max-w-2xl mx-auto py-8 px-4 sm:px-6">
    <div class="space-y-8">
      <div class="text-center space-y-4" v-if="startPage">
        <h1 class="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100">
          {{ startPage.heroTitle }}
        </h1>
        <p class="text-lg text-gray-600 dark:text-gray-400">
          {{ startPage.heroSubtitle }}
        </p>
      </div>

      <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6" v-if="startPage">
        <h2 class="text-lg font-bold text-gray-900 dark:text-gray-100">
          {{ startPage.seoIntroTitle }}
        </h2>
        <p class="mt-2 text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
          {{ startPage.seoIntroBody }}
        </p>
      </div>

      <div
        class="border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4"
        v-if="startPage"
      >
        <p
          class="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300"
        >
          {{ startPage.liveStatusLabel }}: {{ todayInPakistan }}
        </p>
        <p class="mt-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
          {{ liveStatus.title }}
        </p>
        <p class="mt-1 text-sm text-gray-700 dark:text-gray-300">
          {{ liveStatus.description }}
        </p>
      </div>

      <div
        class="bg-linear-to-br from-emerald-50 to-blue-50 dark:from-emerald-900/20 dark:to-blue-900/20 rounded-lg p-6 sm:p-8 space-y-4"
      >
        <div class="space-y-4" v-if="startPage">
          <div class="space-y-2 border-b border-emerald-200 dark:border-emerald-800 pb-4">
            <h2 class="text-xl font-bold text-emerald-700 dark:text-emerald-400">
              {{ startPage.expectedStartHeading }}
            </h2>
            <p class="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {{ startPage.expectedStartTiming }}
            </p>
            <p class="text-sm text-gray-600 dark:text-gray-400 italic">
              {{ startPage.moonSightingDisclaimer }}
            </p>
          </div>

          <p class="text-gray-700 dark:text-gray-300 text-sm">
            {{ startPage.ramadanDurationInfo }}
          </p>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6" v-if="startPage">
          <div class="bg-white dark:bg-gray-800 rounded p-4">
            <p
              class="text-xs font-semibold text-gray-500 dark:text-gray-500 uppercase tracking-wide"
            >
              {{ startPage.hijriYear }}
            </p>
            <p class="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-2">
              {{ startPage.hijriYearValue }}
            </p>
          </div>
          <div class="bg-white dark:bg-gray-800 rounded p-4">
            <p
              class="text-xs font-semibold text-gray-500 dark:text-gray-500 uppercase tracking-wide"
            >
              {{ startPage.daysInRamadan }}
            </p>
            <p class="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-2">
              {{ startPage.daysInRamadanValue }}
            </p>
          </div>
        </div>
      </div>

      <div class="space-y-4" v-if="startPage">
        <h3 class="text-xl font-bold text-gray-900 dark:text-gray-100">
          {{ startPage.moonSightingTitle }}
        </h3>

        <div class="space-y-3">
          <div
            class="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4"
          >
            <p class="font-semibold text-blue-900 dark:text-blue-300 mb-2">
              {{ startPage.officialAnnouncementTitle }}
            </p>
            <p class="text-sm text-blue-800 dark:text-blue-200">
              {{ startPage.officialAnnouncementDesc }}
            </p>
          </div>

          <div
            class="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4"
          >
            <p class="font-semibold text-amber-900 dark:text-amber-300 mb-2">
              {{ startPage.whenToExpectTitle }}
            </p>
            <p class="text-sm text-amber-800 dark:text-amber-200">
              {{ startPage.whenToExpectDesc }}
            </p>
          </div>

          <div
            class="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-4"
          >
            <p class="font-semibold text-green-900 dark:text-green-300 mb-2">
              {{ startPage.stayUpdatedTitle }}
            </p>
            <p class="text-sm text-green-800 dark:text-green-200">
              {{ startPage.stayUpdatedDesc }}
            </p>
          </div>
        </div>
      </div>

      <div class="space-y-4" v-if="startPage">
        <h3 class="text-xl font-bold text-gray-900 dark:text-gray-100">
          {{ startPage.keyDatesTitle }}
        </h3>

        <div class="space-y-3">
          <div class="flex gap-4">
            <div
              class="shrink-0 w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center"
            >
              <span class="text-emerald-700 dark:text-emerald-300 font-bold">1</span>
            </div>
            <div class="flex-1 pt-1">
              <p class="font-semibold text-gray-900 dark:text-gray-100">
                {{ startPage.ramadanBeginsLabel }}
              </p>
              <p class="text-sm text-gray-600 dark:text-gray-400">
                {{ startPage.expectedStartDate }} ({{ startPage.ramadanBeginsDesc }})
              </p>
            </div>
          </div>

          <div class="flex gap-4">
            <div
              class="shrink-0 w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center"
            >
              <span class="text-blue-700 dark:text-blue-300 font-bold">2</span>
            </div>
            <div class="flex-1 pt-1">
              <p class="font-semibold text-gray-900 dark:text-gray-100">
                {{ startPage.lailatQadrLabel }}
              </p>
              <p class="text-sm text-gray-600 dark:text-gray-400">
                {{ startPage.lailatQadrDesc }}
              </p>
            </div>
          </div>

          <div class="flex gap-4">
            <div
              class="shrink-0 w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center"
            >
              <span class="text-purple-700 dark:text-purple-300 font-bold">3</span>
            </div>
            <div class="flex-1 pt-1">
              <p class="font-semibold text-gray-900 dark:text-gray-100">
                {{ startPage.eidLabel }}
              </p>
              <p class="text-sm text-gray-600 dark:text-gray-400">
                {{ startPage.eidDesc }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 space-y-3" v-if="startPage">
        <h3 class="font-bold text-gray-900 dark:text-gray-100">
          {{ startPage.moreResourcesTitle }}
        </h3>
        <ul class="space-y-2">
          <li>
            <NuxtLink
              to="/calendar/"
              class="text-emerald-700 dark:text-emerald-400 hover:underline font-medium"
            >
              {{ startPage.completeCalendarLink }}
            </NuxtLink>
          </li>
          <li>
            <NuxtLink
              to="/"
              class="text-emerald-700 dark:text-emerald-400 hover:underline font-medium"
            >
              {{ startPage.sehriIftarLink }}
            </NuxtLink>
          </li>
          <li>
            <NuxtLink
              to="/shab-e-qadr/"
              class="text-emerald-700 dark:text-emerald-400 hover:underline font-medium"
            >
              {{ startPage.lailatLink }}
            </NuxtLink>
          </li>
          <li>
            <NuxtLink
              to="/dua/"
              class="text-emerald-700 dark:text-emerald-400 hover:underline font-medium"
            >
              {{ startPage.duainLink }}
            </NuxtLink>
          </li>
        </ul>
      </div>

      <div class="space-y-4" v-if="startPage">
        <h3 class="text-xl font-bold text-gray-900 dark:text-gray-100">
          {{ startPage.faqTitle }}
        </h3>

        <div class="space-y-3">
          <details
            v-for="(item, index) in faqItems"
            :key="`faq-${index}`"
            class="group rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 open:bg-white dark:open:bg-gray-900 p-4 transition-colors"
          >
            <summary
              class="flex items-center justify-between gap-3 cursor-pointer list-none font-semibold text-gray-900 dark:text-gray-100"
            >
              <span>{{ item.question }}</span>
              <span
                class="faq-chevron inline-flex h-6 w-6 items-center justify-center rounded-full border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 shrink-0"
                aria-hidden="true"
              >
                <svg
                  viewBox="0 0 24 24"
                  class="h-3.5 w-3.5"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </span>
            </summary>
            <p class="mt-3 text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
              {{ item.answer }}
            </p>
          </details>
        </div>
      </div>

      <div
        class="text-center border-t border-gray-200 dark:border-gray-700 pt-6 text-sm text-gray-600 dark:text-gray-400"
      >
        <p>Data source: Ruet-e-Hilal Committee Pakistan</p>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { seoConfig } from "@/config/seo.config";
import { computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";

const { messages, locale } = useI18n();
const { generateWebPageSchema, addSchema } = useSchemaMarkup();

const PAKISTAN_TIME_ZONE = "Asia/Karachi";
const SIGHTING_DATE = "2026-02-18";
const FIRST_ROZA_DATE = "2026-02-19";
const EXPECTED_END_DATE = "2026-03-20";

function getTodayInPakistan() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: PAKISTAN_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

useHead({
  title: seoConfig["ramadan-start"].title,
  meta: [
    {
      name: "description",
      content: seoConfig["ramadan-start"].description,
    },
    {
      name: "keywords",
      content:
        "pakistan mein roza kab hai, ramzan kab hai 2026, pehla roza kab hoga, ramazan 2026 date pakistan, ramadan moon sighting pakistan, ruet e hilal committee",
    },
    {
      property: "og:title",
      content: seoConfig["ramadan-start"].title,
    },
    {
      property: "og:description",
      content: seoConfig["ramadan-start"].description,
    },
    {
      property: "og:type",
      content: "website",
    },
    {
      property: "og:url",
      content: "https://rozay.today/ramadan-start/",
    },
    {
      property: "og:site_name",
      content: "Rozay - Islamic Calendar & Ramadan Guide",
    },
    {
      name: "twitter:card",
      content: "summary_large_image",
    },
    {
      name: "twitter:title",
      content: seoConfig["ramadan-start"].title,
    },
    {
      name: "twitter:description",
      content: seoConfig["ramadan-start"].description,
    },
  ],
  link: [
    {
      rel: "canonical",
      href: seoConfig["ramadan-start"].canonical,
    },
  ],
});

const startPage = computed(() => messages.value[locale.value]?.pages?.ramadanStart);
const todayInPakistan = computed(() => getTodayInPakistan());
const faqItems = computed(() => {
  if (!startPage.value) return [];

  const page = startPage.value as Record<string, string>;
  const items: Array<{ question: string; answer: string }> = [];

  for (let i = 1; i <= 10; i += 1) {
    const question = page[`q${i}Title`];
    const answer = page[`q${i}Answer`];
    if (question && answer) {
      items.push({ question, answer });
    }
  }

  return items;
});

useHead(() => ({
  script:
    faqItems.value.length > 0
      ? [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: faqItems.value.map((item) => ({
                "@type": "Question",
                name: item.question,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: item.answer,
                },
              })),
            }),
          },
        ]
      : [],
}));

const liveStatus = computed(() => {
  if (!startPage.value) {
    return { title: "", description: "" };
  }

  const today = todayInPakistan.value;

  if (today < SIGHTING_DATE) {
    return {
      title: startPage.value.statusBeforeTitle,
      description: startPage.value.statusBeforeDesc,
    };
  }

  if (today === SIGHTING_DATE) {
    return {
      title: startPage.value.statusSightingTitle,
      description: startPage.value.statusSightingDesc,
    };
  }

  if (today >= FIRST_ROZA_DATE && today <= EXPECTED_END_DATE) {
    return {
      title: startPage.value.statusStartedTitle,
      description: startPage.value.statusStartedDesc,
    };
  }

  return {
    title: startPage.value.statusEndedTitle,
    description: startPage.value.statusEndedDesc,
  };
});

const webPageSchema = computed(() =>
  generateWebPageSchema({
    name: seoConfig["ramadan-start"].title,
    description: seoConfig["ramadan-start"].description,
    url: seoConfig["ramadan-start"].canonical,
  }),
);

onMounted(() => {
  addSchema(webPageSchema.value);
});
</script>

<style scoped>
details {
  transition: all 0.3s ease;
}

details[open] {
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

details summary::-webkit-details-marker {
  display: none;
}

.faq-chevron {
  transition: transform 0.2s ease;
}

details[open] .faq-chevron {
  transform: rotate(90deg);
}
</style>

